import { cn } from '@/lib/utils';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import React, { useEffect, useId, useRef, useState } from 'react';

export interface AsyncSearchableSelectOption {
  value: string;
  label: string;
}

interface AsyncSearchableSelectProps {
  loadOptions: (inputValue: string) => Promise<AsyncSearchableSelectOption[]>;
  value?: string;
  onValueChange?: (value: string) => void;
  onOptionSelect?: (option: AsyncSearchableSelectOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  defaultOptions?: AsyncSearchableSelectOption[];
  initialLabel?: string;
}

export const AsyncSearchableSelect: React.FC<AsyncSearchableSelectProps> = ({
  loadOptions,
  value,
  onValueChange,
  onOptionSelect,
  placeholder = "Select an option...",
  disabled = false,
  className,
  emptyMessage = "No results found",
  searchPlaceholder = "Type to search...",
  defaultOptions = [],
  initialLabel
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<AsyncSearchableSelectOption[]>(defaultOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRequestRef = useRef(0);

  // Initialize selected label
  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    if (initialLabel) {
      setSelectedLabel(initialLabel);
    } else if (defaultOptions.length > 0) {
      const option = defaultOptions.find(o => o.value === value);
      if (option) setSelectedLabel(option.label);
    }
  }, [value, initialLabel, defaultOptions]);

  useEffect(() => {
    if (!value || initialLabel) return;
    const option = options.find(o => o.value === value);
    if (option && option.label !== selectedLabel) {
      setSelectedLabel(option.label);
    }
  }, [value, initialLabel, options, selectedLabel]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      latestRequestRef.current += 1;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options, searchTerm]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
      highlightedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex]);

  // Fetch options with debounce
  const fetchOptions = async (query: string) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);
    try {
      const results = await loadOptions(query);
      if (latestRequestRef.current !== requestId) return;
      setOptions(results);
    } catch (error) {
      console.error('Error loading options:', error);
      if (latestRequestRef.current !== requestId) return;
      setOptions([]);
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchTerm(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchOptions(query);
    }, 300);
  };

  // Perform initial fetch when opening if no options (and no search term)
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setHighlightedIndex(-1);
      if (options.length === 0 && !searchTerm) {
        fetchOptions('');
      }
    }
  };

  const handleSelectOption = (option: AsyncSearchableSelectOption) => {
    onValueChange?.(option.value);
    onOptionSelect?.(option);
    setSelectedLabel(option.label);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange?.('');
    onOptionSelect?.(null);
    setSelectedLabel('');
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          if (options.length === 0 && !searchTerm) {
            fetchOptions('');
          }
          return;
        }
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleSelectOption(options[highlightedIndex]);
          return;
        }
        if (options.length === 1) {
          handleSelectOption(options[0]);
        }
        break;
      case 'Escape':
        if (isOpen || searchTerm) {
          e.preventDefault();
          setIsOpen(false);
          setSearchTerm('');
          setHighlightedIndex(-1);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          if (options.length === 0 && !searchTerm) {
            fetchOptions('');
          }
          return;
        }
        if (options.length === 0) return;
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen || options.length === 0) return;
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
    }
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          "cursor-text"
        )}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : selectedLabel}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={isOpen ? searchPlaceholder : selectedLabel ? '' : placeholder}
            disabled={disabled}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined
            }
            className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </div>
        
        <div className="flex items-center gap-1">
          {value && !disabled && (
            <button type="button" onClick={handleClear} className="text-muted-foreground hover:text-foreground p-1">
              <X className="h-3 w-3" />
            </button>
          )}
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin opacity-50" />
          ) : (
            <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
          <div ref={listRef} className="p-1" role="listbox" id={listboxId}>
            {isLoading ? (
              <div className="py-2 px-3 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="py-2 px-3 text-sm text-muted-foreground text-center">{emptyMessage}</div>
            ) : (
              options.map((option, index) => (
                <div
                  key={option.value}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={option.value === value}
                  className={cn(
                    "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                    index === highlightedIndex && "bg-accent text-accent-foreground",
                    option.value === value && "bg-accent text-accent-foreground"
                  )}
                  onClick={() => handleSelectOption(option)}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {option.value === value && <Check className="h-4 w-4" />}
                  </span>
                  <span className="truncate">{option.label}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
